/**
 * Speed limit signs along the way being driven: the round Swedish sign, a
 * red ring on white with the limit in black, on a post at the right-hand
 * side of the road — where the limit changes, and again after each turn.
 *
 * Only on the route the autopilot drives: the streamed tiles do not know the
 * roads' limits, and the route does (see `loaders/routeService.ts`).
 */

import {
  CanvasTexture,
  CircleGeometry,
  Color,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  SRGBColorSpace,
} from 'three';
import type { Scene } from 'three';
import type { Route } from '../core/nav/route.js';
import { pointAt, routeToGeo } from '../core/nav/route.js';

/** One sign: where it stands, which way it faces, and its limit. */
export interface SpeedSign {
  lat: number;
  lon: number;
  /** Scene yaw that turns the sign's face to the traffic coming at it. */
  yaw: number;
  kmh: number;
}

/** How far past a change of limit, or a turn, the sign stands, metres. */
const PAST = 18;
/** How far right of the route's line the post stands, metres: further off on the fast roads. */
const OFFSET_TOWN = 5.5;
const OFFSET_FAST = 9;
const POST_HEIGHT = 2.1;
const SIGN_RADIUS = 0.38;

/** Where the signs go on a route: each change of limit, and after each junction turned at. */
export function placeSigns(route: Route): SpeedSign[] {
  const kmhAt = (i: number) => {
    const v = route.speeds[i];
    return Number.isFinite(v) ? Math.round((v * 3.6) / 10) * 10 : undefined;
  };
  const at: Array<{ s: number; kmh: number }> = [];
  let last: number | undefined;
  for (let i = 0; i + 1 < route.along.length; i++) {
    const kmh = kmhAt(i);
    if (kmh === undefined) continue;
    if (kmh !== last) at.push({ s: route.along[i] + PAST, kmh });
    last = kmh;
  }
  // Repeated after each turn, where a driver has just come onto the road.
  for (const j of route.junctions) {
    const s = j.at + PAST;
    const i = pointAt(route, s).stretch;
    const kmh = kmhAt(i);
    if (kmh !== undefined && !at.some(a => Math.abs(a.s - s) < 60)) at.push({ s, kmh });
  }
  return at.filter(a => a.s < route.length - 5).map(({ s, kmh }) => {
    const p = pointAt(route, s);
    const off = kmh >= 90 ? OFFSET_FAST : OFFSET_TOWN;
    // The right-hand side: the right normal of the direction of travel.
    const geo = routeToGeo(route, p.east + p.dn * off, p.north - p.de * off);
    // Facing back down the road: the sign's front, scene +Z, turned to (-de, dn) in scene axes.
    return { lat: geo.lat, lon: geo.lon, yaw: Math.atan2(-p.de, p.dn), kmh };
  });
}

const faces = new Map<number, MeshStandardMaterial>();

/** The sign's face for a limit, painted once. */
function faceFor(kmh: number): MeshStandardMaterial {
  let m = faces.get(kmh);
  if (m !== undefined) return m;
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  ctx.fillStyle = '#d4202a';
  ctx.beginPath(); ctx.arc(c, c, c - 1, 0, Math.PI * 2); ctx.fill();
  // The Swedish sign's field is yellow, inside the red ring.
  ctx.fillStyle = '#f7d117';
  ctx.beginPath(); ctx.arc(c, c, c * 0.74, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#111111';
  ctx.font = `700 ${kmh >= 100 ? 50 : 60}px "DIN Alternate", "Arial Narrow", system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(kmh), c, c + 3);
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  m = new MeshStandardMaterial({ map: texture, roughness: 0.5 });
  faces.set(kmh, m);
  return m;
}

const postGeometry = new CylinderGeometry(0.04, 0.04, POST_HEIGHT, 8).translate(0, POST_HEIGHT / 2, 0);
const postMaterial = new MeshStandardMaterial({ color: new Color(0x9aa0a6), metalness: 0.6, roughness: 0.4 });
const faceGeometry = new CircleGeometry(SIGN_RADIUS, 28);
const backMaterial = new MeshStandardMaterial({ color: new Color(0x8a9096), metalness: 0.5, roughness: 0.5 });

/** The signs standing in the world, placed as the ground under them streams in. */
export class SpeedSigns {
  private readonly group = new Group();
  private signs: Array<{ sign: SpeedSign; root: Group; ground?: number }> = [];

  constructor(scene: Scene) {
    scene.add(this.group);
  }

  set(signs: readonly SpeedSign[]): void {
    this.clear();
    for (const sign of signs) {
      const root = new Group();
      root.add(new Mesh(postGeometry, postMaterial));
      const face = new Mesh(faceGeometry, faceFor(sign.kmh));
      face.position.set(0, POST_HEIGHT + SIGN_RADIUS - 0.1, 0.05);
      const back = new Mesh(faceGeometry, backMaterial);
      back.position.set(0, POST_HEIGHT + SIGN_RADIUS - 0.1, 0.045);
      back.rotation.y = Math.PI;
      root.add(face, back);
      root.rotation.y = sign.yaw;
      root.visible = false;
      root.traverse(o => { o.castShadow = true; });
      this.group.add(root);
      this.signs.push({ sign, root });
    }
  }

  clear(): void {
    for (const s of this.signs) this.group.remove(s.root);
    this.signs = [];
  }

  /** Stand the signs near the player where the world is, on the ground under them. */
  update(toLocal: (lat: number, lon: number) => { east: number; north: number }, near: { east: number; north: number },
    ground: (lat: number, lon: number) => number | undefined): void {
    for (const s of this.signs) {
      const at = toLocal(s.sign.lat, s.sign.lon);
      const far = Math.hypot(at.east - near.east, at.north - near.north) > 800;
      if (!far && s.ground === undefined) s.ground = ground(s.sign.lat, s.sign.lon);
      s.root.visible = !far && s.ground !== undefined;
      if (s.root.visible) s.root.position.set(at.east, s.ground!, -at.north);
    }
  }
}

/** The round sign as a little HTML badge, for the corner of the screen. */
export function limitBadge(kmh: number | undefined): string {
  if (kmh === undefined) return '';
  return `<span style="display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;` +
    `border-radius:50%;border:4px solid #d4202a;background:#f7d117;color:#111;font:800 13px system-ui;` +
    `box-sizing:border-box;vertical-align:middle;margin-left:10px">${kmh}</span>`;
}
